import type { IFuseOptions } from 'fuse.js'
import Fuse from 'fuse.js'
import { useCallback, useMemo } from 'react'

/** Shortest query the admin search boxes will act on. */
export const MIN_SEARCH_LENGTH = 2

/** Hits returned per query — enough to fill a dropdown, cheap to render. */
const MAX_RESULTS = 20

/** Fuse tuning shared by every admin search box. */
export const FUZZY_SEARCH_DEFAULTS = {
  threshold: 0.4, // Lower = stricter matching
  minMatchCharLength: MIN_SEARCH_LENGTH,
  includeScore: true,
  shouldSort: true,
} as const

export interface FuzzySearchResult<T> {
  item: T
  score: number
}

export interface UseFuzzySearchResult<T> {
  /** Returns [] until the query reaches MIN_SEARCH_LENGTH characters. */
  search: (query: string) => FuzzySearchResult<T>[]
  minSearchLength: number
}

/**
 * Fuse.js-backed fuzzy search over an in-memory collection.
 *
 * The Fuse index is rebuilt only when `items` changes, so callers should hand
 * over a memoized array. `keys` is read once per index build — pass a
 * module-level constant rather than an inline literal.
 */
export function useFuzzySearch<T>(
  items: T[],
  keys: NonNullable<IFuseOptions<T>['keys']>,
  options: { limit?: number } = {},
): UseFuzzySearchResult<T> {
  const limit = options.limit ?? MAX_RESULTS

  const fuse = useMemo(
    () => new Fuse(items, { ...FUZZY_SEARCH_DEFAULTS, keys }),
    [items, keys],
  )

  const search = useCallback(
    (query: string): FuzzySearchResult<T>[] => {
      if (query.length < MIN_SEARCH_LENGTH) {
        return []
      }

      return fuse.search(query, { limit }).map((result) => ({
        item: result.item,
        score: result.score ?? 1,
      }))
    },
    [fuse, limit],
  )

  return { search, minSearchLength: MIN_SEARCH_LENGTH }
}
